import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Skeleton } from "primereact/skeleton";
import { Dropdown } from "primereact/dropdown";
import { currencyList } from "~/utils/currency";
import CountryDropdown from "~/components/countryDropDown";
import { useDispatch } from "react-redux";
import { setEmployeeData } from "../../actions/employeeSlice";
import { RadioButton } from "primereact/radiobutton";

const BankInformationForm = ({
  bankInformation,
  handleChange,
  isLoading = false,
  isEdit,
}) => {
  const {
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: bankInformation,
  });
  useEffect(() => {
    const safeData = bankInformation || {};
    reset(safeData);
  }, [bankInformation, reset]);

  const dispatch = useDispatch();
  const formError = Object.keys(errors);
  useEffect(() => {
    if (formError.length) {
      dispatch(
        setEmployeeData({
          key: "nextBtnEnable",
          value: false,
        }),
      );
    } else {
      dispatch(
        setEmployeeData({
          key: "nextBtnEnable",
          value: true,
        }),
      );
    }
  }, [formError]);
  return (
    <>
      <div className="formgrid grid">
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label mb-2">Account's holder name</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="bank_account_holder_name"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={bankInformation?.bank_account_holder_name || ""}
                  onChange={(e) => {
                    handleChange("bank_account_holder_name", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter full name"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.bank_account_holder_name ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.bank_account_holder_name && (
            <small className="p-error">
              {errors.bank_account_holder_name.message}
            </small>
          )}
        </div>

        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label">Bank name</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="bank_name"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={bankInformation?.bank_name || ""}
                  onChange={(e) => {
                    handleChange("bank_name", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter bank name"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.bank_name ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.bank_name && (
            <small className="p-error">{errors.bank_name.message}</small>
          )}
        </div>

        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label">Bank Account Number / IBAN</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="bank_account_number"
              control={control}
              rules={{
                pattern: {
                  value: /^[A-Z]{2}[0-9A-Z]{1,30}$/,
                  message:
                    "Format: 2 letters (country code) followed by digits (A-Z, 0-9)",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={bankInformation?.bank_account_number || ""}
                  onChange={(e) => {
                    handleChange("bank_account_number", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter account number or IBAN"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.bank_account_number ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.bank_account_number && (
            <small className="p-error">
              {errors.bank_account_number.message}
            </small>
          )}
        </div>

        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label">SWIFT/BIC Code</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="bank_swift_bic"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={bankInformation?.bank_swift_bic || ""}
                  onChange={(e) => {
                    const updatedValue = e.target.value.replace(
                      /[^a-zA-Z0-9]/g,
                      "",
                    );
                    handleChange("bank_swift_bic", updatedValue);
                    field.onChange(updatedValue);
                  }}
                  type="text"
                  placeholder="Enter international bank code"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.bank_swift_bic ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.bank_swift_bic && (
            <small className="p-error">{errors.bank_swift_bic.message}</small>
          )}
        </div>

        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label">Account currency</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="bank_account_currency"
              control={control}
              render={({ field }) => {
                return (
                  <Dropdown
                    {...field}
                    value={field.value}
                    onChange={(e) => {
                      const newValue = e.value;
                      handleChange("bank_account_currency", newValue);
                      field.onChange(newValue);
                    }}
                    options={currencyList}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select currency"
                    className={`${isEdit && "input-field-dropdown"} w-full ${!isEdit && "dropdown-field"} ${
                      !isEdit && "disable"
                    }`}
                    filter
                    disabled={!isEdit}
                    itemTemplate={(option) => (
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId={option?.value}
                          value={option?.value}
                          checked={field.value === option?.value}
                          onChange={() => {
                            handleChange("currency", option?.value);
                            field.onChange(option?.value);
                          }}
                        />
                        <label htmlFor={option?.value} className="ml-2">
                          ({option?.value}) {option?.label}
                        </label>
                      </div>
                    )}
                    valueTemplate={(selectedOption, props) => {
                      const selected = currencyList.find(
                        (c) => c?.value === selectedOption?.value,
                      );
                      return selected ? (
                        <span>
                          ({selected?.value}) {selected?.label}
                        </span>
                      ) : (
                        <span className="p-dropdown-placeholder">
                          {props.placeholder}
                        </span>
                      );
                    }}
                  />
                );
              }}
            />
          )}

          {errors.bank_account_currency && (
            <small className="p-error">
              {errors.bank_account_currency.message}
            </small>
          )}
        </div>

        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label mb-2">Bank Branch / Address</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="bank_branch_address"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={bankInformation?.bank_branch_address || ""}
                  onChange={(e) => {
                    handleChange("bank_branch_address", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter branch or address"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.bank_branch_address ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.bank_branch_address && (
            <small className="p-error">
              {errors.bank_branch_address.message}
            </small>
          )}
        </div>

        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label">Bank country</label>
          {isLoading ? (
            <Skeleton className="p-4"></Skeleton>
          ) : (
            <Controller
              name="bank_country"
              control={control}
              render={({ field }) => (
                <CountryDropdown
                  {...field}
                  value={bankInformation?.bank_country || {}}
                  onChange={(e) => {
                    field.onChange(e.value);
                    handleChange("bank_country", e.value);
                  }}
                  filter
                  disabled={!isEdit}
                  className={`w-full ${isEdit && "input-field-dropdown"}  ${!isEdit && "dropdown-field"} ${!isEdit && "disable"} ${
                    errors.country ? "p-invalid" : ""
                  }`}
                  error={errors?.bank_country}
                />
              )}
            />
          )}
          {errors?.bank_country && (
            <small className="p-error">{errors?.bank_country?.message}</small>
          )}
        </div>
      </div>
    </>
  );
};
BankInformationForm.propTypes = {
  bankInformation: PropTypes.object,
  handleChange: PropTypes.func,
  isLoading: PropTypes.bool,
  isEdit: PropTypes.bool,
};
export default BankInformationForm;
